import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

export interface DirectusCustomerDetails {
  id: string;
  msisdn: string;
  savedPosts: {
    id: string;
    title: string;
    channelImageUri: string;
    channelName: string;
    formattedText: string;
    publishedAt: string;
    description: string;
    content: string;
    coverImage: string;
  }[];
  [key: string]: unknown; // Allow additional properties
}

export const addToMyList = async (
  postId: string,
  msisdn: string
): Promise<{ success: boolean; error?: string; id?: string }> => {
  try {
    const correlationId = `correlation-${uuidv4()}`;
    const result = await axios.post(
      `${process.env.REACT_APP_SATURN_BFF_URL}customers/${msisdn}/add-post`,
      {
        postId,
        msisdn,
      },
      {
        headers: {
          correlationId: correlationId,
          Authorization: `Bearer ${process.env.SATURN_BFF_TOKEN}`,
        },
      }
    );

    const { data } = result;

    // Handle nested message.message structure from BFF
    const responseData = data.message?.message ?? data.message ?? data;

    // Extract linkId from various possible locations
    const linkId = responseData?.linkId ?? responseData?.id ?? data?.linkId ?? data?.id;

    return { success: true, id: linkId };
  } catch (error) {
    console.error(`Failed to add item to My List:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to add item to My List',
    };
  }
};

export const removeItemFromList = async (
  linkId: string | number,
  postId: string,
  msisdn: string
): Promise<{ success: boolean }> => {
  try {
    const correlationId = `correlation-${uuidv4()}`;
    await axios.delete(`${process.env.REACT_APP_SATURN_BFF_URL}customers/${msisdn}/delete-post`, {
      headers: {
        correlationId: correlationId,
        Authorization: `Bearer ${process.env.SATURN_BFF_TOKEN}`,
      },
      data: {
        linkId: String(linkId),
        postId,
      },
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to remove post from list: ', error);
    return { success: false };
  }
};

export const getCustomerDetails = async (
  msisdn: string,
  categories?: string[]
): Promise<DirectusCustomerDetails | undefined> => {
  try {
    const correlationId = `correlation-${uuidv4()}`;
    const categoriesParam = categories && categories.length > 0 ? categories.join(',') : undefined;
    const url = `${process.env.REACT_APP_SATURN_BFF_URL}customers/${msisdn}/saved-posts${categoriesParam ? `?categories=${categoriesParam}` : ''}`;

    const result = await axios.get(url, {
      headers: {
        correlationId: correlationId,
        Authorization: `Bearer ${process.env.SATURN_BFF_TOKEN}`,
      },
    });

    const { data } = result;

    // Extract data from BFF nested message.message structure
    const responseData = data.message?.message ?? data.message ?? data;

    // BFF should return data in the expected format, but we map it to ensure compatibility
    if (!responseData) {
      return undefined;
    }

    // If BFF returns the data already formatted, use it directly
    // Otherwise, map from the Directus-like structure
    const mappedData = Array.isArray(responseData) ? responseData[0] : responseData;

    return {
      id: mappedData.id,
      msisdn: mappedData.msisdn ?? msisdn,
      savedPosts: mappedData.savedPosts ?? mappedData.saved_posts ?? [],
      ...mappedData,
    };
  } catch (error) {
    console.error(`Failed to fetch customer details:`, error);
    return undefined;
  }
};
